import React, { useEffect, useMemo, useState } from 'react';
import { BRU_EVIDENCES_COLUMNS } from 'constants/reports';
import { BruEvidenceData } from 'store/ducks/Reporting/types';
import { useFilterOptions } from 'utils/hooks/useFilterOptions';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import { useModuleOptions } from 'utils/hooks/useModule';
import * as Styled from './BruEvidences.styled';
import { useReportExport } from 'utils/hooks/useReportExport';
import { stripHtmlTags } from 'utils/stripHtmlTags';

const BruEvidences: React.FC = () => {
  const { loading } = useFilterOptions();
  const [data, setData] = useState<BruEvidenceData[]>([]);
  const { registerReport, clearReport } = useReportExport();

  const columns = useMemo(() => BRU_EVIDENCES_COLUMNS, []);
  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const filterParams = Helpers.toApiFilters(orgContext);
  const normalizedFilters = Helpers.normalizeFilters(
    Helpers.toApiFilters(orgContext)
  );
  const industry = orgContext?.industryType || [];

  const { moduleOptionsState, fetchModules } = useModuleOptions(industry);

  const handleDisplay = async () => {
    try {
      if (!orgContext || Object.keys(orgContext).length === 0) {
        return;
      }

      const hasFilters = Object.keys(filterParams).length > 0;

      if (!hasFilters) {
        return;
      }

      const modules =
        moduleOptionsState.length > 0
          ? moduleOptionsState
          : await fetchModules();

      const selectedModule = modules.find(
        (module) => Number(module.value) === Number(normalizedFilters.module)
      );

      const apiFilters = {
        ...normalizedFilters,
        module: selectedModule?.label ?? normalizedFilters.module
      };

      const response = await ApiService.getBruEvidences(lang, apiFilters);

      const responseData: BruEvidenceData[] = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      const formattedData = responseData.map(
        (item: BruEvidenceData, index: number) => {
          const evidences = Object.values(item.EVIDENCES ?? {});

          return {
            ...item,
            value: stripHtmlTags(item.value ?? ''),
            key: item.id || index,
            module: selectedModule?.label ?? String(normalizedFilters.module),
            evd_description: evidences.map((e) =>
              stripHtmlTags(e.description ?? '')
            ),
            evd_approved: evidences.map((e) => e.approved ?? false),
            evd_approver_name: evidences.map((e) => e.approver_name ?? ''),
            evd_reviewed: evidences.map((e) => e.reviewed ?? false),
            evd_reviewer_name: evidences.map((e) => e.reviewer_name ?? ''),
            evd_valid_from: evidences.map((e) => e.valid_from ?? ''),
            evd_valid_to: evidences.map((e) => e.valid_to ?? ''),
            evd_sol_desc: evidences.map((e) => stripHtmlTags(e.sol_desc ?? '')),
            evd_created_by: evidences.map((e) => e.created_by ?? ''),
            evd_created_on: evidences.map((e) => e.created_on ?? ''),
            evd_unused: evidences.map((e) => e.evd_unused ?? false)
          };
        }
      );

      setData(formattedData);
    } catch (error) {
      console.error('Failed to fetch BRU Evidences data:', error);
    }
  };

  useEffect(() => {
    registerReport(data, columns, 'BRU_Evidences', 'BRU Evidences');
  }, [data, columns, registerReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  useEffect(() => {
    if (!lang) return;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    handleDisplay();
  }, [lang, orgContext]);

  return (
    <Styled.VerticalSpace direction='vertical' size='large'>
      <CustomTable
        drawTable={true}
        itemsCount={data.length}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1600, y: 400 }}
        loading={loading}
        rowKey={(record: BruEvidenceData) => record.key}
        sticky
      />
    </Styled.VerticalSpace>
  );
};

export default BruEvidences;
